import React from 'react'
import { useNavigate, useParams } from 'react-router'
import { UnderwritingOffer } from '_gql'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import { BASIC_BUTTON_TYPE_FLAT } from '@divvy-web/skylab.basicbutton'
import Icon from '@divvy-web/skylab.icon'

import BillBrandLogo from '@sputnik/components/BillBrandLogo'
import GoToDashboardButton from '@sputnik/components/GoToDashboardButton'
import ImageWrapper from '@sputnik/components/ImageWrapper'
import { MarketingPill } from '@sputnik/components/MarketingContent'
import useDivvyUUID from '@sputnik/hooks/useDivvyUUID'
import { PATHNAME_CONSTS, PRODUCT_CONST_STRINGS } from '@sputnik/resources/constants'

import { useGetApplicationDecisions } from './GetApplicationDecisions.gql'
import OfferTile from './OfferTile'
import styles from './styles.module.scss'

const CreditLineOffersPage = () => {
  const [companyDivvyUuid] = useDivvyUUID()
  const [getClassName] = useNameFormatter('CreditLineOffersPage')
  const { appId } = useParams()
  const navigate = useNavigate()

  const { data: decisionsData } = useGetApplicationDecisions({
    skip: !companyDivvyUuid,
    variables: { companyId: companyDivvyUuid },
  })

  const decisions = decisionsData?.decisions || []
  const offersPathname = `/app/${appId}/${PATHNAME_CONSTS.OFFERS_PATH}`

  const standardLineOffer = decisions?.find((decision) => decision?.offerType === UnderwritingOffer.FIXED_LINE)
  const variableLineOffer = decisions?.find((decision) => decision?.offerType === UnderwritingOffer.VARIABLE_LINE)
  const securedLineOffer = decisions?.find((decision) => decision?.offerType === UnderwritingOffer.SECURED_LINE)

  const offers = {
    standard: standardLineOffer,
    variable: variableLineOffer,
    secured: securedLineOffer,
  }

  const { hasStandardOffer, hasVariableOffer, hasSecuredOffer } = {
    hasStandardOffer: standardLineOffer !== undefined,
    hasVariableOffer: variableLineOffer !== undefined,
    hasSecuredOffer: securedLineOffer !== undefined,
  }

  const offerCount = [hasStandardOffer, hasVariableOffer, hasSecuredOffer].filter(Boolean).length

  return (
    <div className={styles[getClassName('')]}>
      <BillBrandLogo />
      <div className={styles[getClassName('content')]}>
        <div className={styles[getClassName('title')]}>
          <FormattedMessage
            defaultMessage='Congrats! You pre-qualify for {numOffers, plural, one {# offer} other {# offers}}.'
            id='sputnik.CreditLineOffersPage__98x1jF'
            values={{ numOffers: offerCount }}
          />
        </div>
        <div className={styles[getClassName('subtitle')]}>
          <FormattedMessage
            defaultMessage='Based on the information from your credit application, you’re eligible for the following credit offers:'
            id='sputnik.CreditLineOffersPage__et15an'
          />
        </div>
        <div className={styles[getClassName('offers-container')]}>
          <div className={styles[getClassName('included-with-offers')]}>
            <div className={styles[getClassName('included-with-offers-title')]}>
              <FormattedMessage
                defaultMessage='Included with all offers:'
                id='sputnik.CreditLineOffersPage__MJHQ84'
              />
            </div>
            <ul className={styles[getClassName('included-with-offers-bullet-points')]}>
              <li>
                <div>
                  <FormattedMessage
                    defaultMessage='Earn rewards as you spend'
                    id='sputnik.CreditLineOffersPage__g+2llw'
                  />
                </div>
              </li>
              <li>
                <div>
                  <FormattedMessage
                    defaultMessage='Free access to {BILL_SPEND_AND_EXPENSE} platform'
                    id='sputnik.CreditLineOffersPage__AQXkVc'
                    values={{ BILL_SPEND_AND_EXPENSE: PRODUCT_CONST_STRINGS.BILL_SPEND_AND_EXPENSE }}
                  />
                </div>
              </li>
              <li>
                <div>
                  <FormattedMessage
                    defaultMessage='Pay zero interest fees'
                    id='sputnik.CreditLineOffersPage__S4fbfk'
                  />
                </div>
              </li>
            </ul>
          </div>
          <div className={styles[getClassName('offers')]}>
            {hasStandardOffer && (
              <OfferTile
                approvedAmount={offers.standard?.approvedAmountInCents}
                caption={
                  <>
                    <div>
                      <FormattedMessage
                        defaultMessage="Your credit limit is fixed and typically won't change."
                        id='sputnik.CreditLineOffersPage__kT0CVZ'
                      />
                    </div>
                    <div>
                      <FormattedMessage
                        defaultMessage='Great for companies that require a steady credit limit from month to month.'
                        id='sputnik.CreditLineOffersPage__YFvyE/'
                      />
                    </div>
                  </>
                }
                dataTestId='standard'
                image={
                  <ImageWrapper
                    alt='hand holding a credit card'
                    className={getClassName('')}
                    imageName={'spot-standard-credit-line'}
                  />
                }
                pillWithImage={
                  <MarketingPill
                    text={
                      <>
                        <Icon name='ai-insight' />
                        <FormattedMessage
                          defaultMessage='The OG'
                          id='sputnik.CreditLineOffersPage__S9fbpp'
                        />
                      </>
                    }
                  />
                }
                title={
                  <FormattedMessage
                    defaultMessage='Standard credit line'
                    id='sputnik.CreditLineOffersPage__WQCeHH'
                  />
                }
                onViewOfferDetailsClick={() =>
                  navigate(`${offersPathname}/${PATHNAME_CONSTS.STANDARD_CREDIT_LINE_PATH}`)
                }
              />
            )}
            {hasVariableOffer && (
              <OfferTile
                approvedAmount={offers.variable?.approvedAmountInCents}
                caption={
                  <>
                    <div>
                      <FormattedMessage
                        defaultMessage='Your credit limit may adjust based on your bank account balance.'
                        id='sputnik.CreditLineOffersPage__1hE+fo'
                      />
                    </div>
                    <div>
                      <FormattedMessage
                        defaultMessage='Ideal for companies that need a flexible credit limit that grows with their business.'
                        id='sputnik.CreditLineOffersPage__+j1YHY'
                      />
                    </div>
                  </>
                }
                dataTestId='variable'
                image={
                  <ImageWrapper
                    alt='hands opening a wallet with cash inside'
                    className={getClassName('')}
                    imageName={'spot-variable-credit-line'}
                  />
                }
                pillWithImage={
                  <MarketingPill
                    text={
                      <>
                        <Icon name='reports' />
                        <FormattedMessage
                          defaultMessage='The growth engine'
                          id='sputnik.CreditLineOffersPage__S94b4a'
                        />
                      </>
                    }
                  />
                }
                title={
                  <FormattedMessage
                    defaultMessage='Variable credit line'
                    id='sputnik.CreditLineOffersPage__8ugHZu'
                  />
                }
                onViewOfferDetailsClick={() =>
                  navigate(`${offersPathname}/${PATHNAME_CONSTS.VARIABLE_CREDIT_LINE_PATH}`)
                }
              />
            )}
            {hasSecuredOffer && (
              <OfferTile
                approvedAmountPlaceHolder={
                  <FormattedMessage
                    defaultMessage='You choose the credit limit'
                    id='sputnik.CreditLineOffersPage__9QCeLo'
                  />
                }
                caption={
                  <div>
                    <FormattedMessage
                      defaultMessage='Credit limit is funded by your own cash deposit, which you can adjust at any time.'
                      id='sputnik.CreditLineOffersPage__IvzXNC'
                    />
                  </div>
                }
                dataTestId='secured'
                image={
                  <ImageWrapper
                    alt='a scale with money on one side and a credit card on the other side'
                    className={getClassName('')}
                    imageName={'spot-secured-credit-line'}
                  />
                }
                pillWithImage={
                  <MarketingPill
                    text={
                      <>
                        <Icon name='finance-tools' />
                        <FormattedMessage
                          defaultMessage='The rewards machine'
                          id='sputnik.CreditLineOffersPage__4Bz14c'
                        />
                      </>
                    }
                  />
                }
                title={
                  <FormattedMessage
                    defaultMessage='Prepayment'
                    id='sputnik.CreditLineOffersPage__Zm8wJX'
                  />
                }
                onViewOfferDetailsClick={() => navigate(`${offersPathname}/${PATHNAME_CONSTS.PREPAY_OFFER_PATH}`)}
              />
            )}
          </div>
        </div>
      </div>
      <div className={styles[getClassName('return-to-dashboard-button')]}>
        <GoToDashboardButton
          buttonType={BASIC_BUTTON_TYPE_FLAT}
          componentName={getClassName('')}
        />
      </div>
    </div>
  )
}

export default CreditLineOffersPage
